import { useState } from "react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import ScrollReveal from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Shield, Clock, Mail, Phone, CalendarDays } from "lucide-react";

const Contact = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    business_name: "", industry: "", biggest_challenge: "", referral_source: "",
  });

  const update = (key: string, val: string) => setForm((p) => ({ ...p, [key]: val }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast({ title: "Please fill in required fields", variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("leads").insert([form]);
    setLoading(false);
    if (error) {
      toast({ title: "Something went wrong", description: error.message, variant: "destructive" });
    } else {
      setSubmitted(true);
      toast({ title: "Thank you!", description: "We'll be in touch within 24 hours." });
    }
  };

  return (
    <Layout>
      <SEOHead title="Contact Xentic Solutions | Book Free AI Automation Consultation" description="Book a free consultation. Tell us about your business and we'll show you how AI automation can help." path="/contact" />

      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute top-1/4 -right-32 w-96 h-96 bg-accent/15 rounded-full blur-[120px] animate-blob" />
        <div className="container relative z-10">
          <div className="grid md:grid-cols-2 gap-12">
            <ScrollReveal>
              <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Get In Touch</p>
              <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">Let's Talk About <span className="text-gradient">Your Business</span></h1>
              <p className="text-muted-foreground mb-8">Fill out the form and we'll get back to you within 24 hours with a custom automation plan, no obligation.</p>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-green-400 shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                  <a href="https://wa.me/19178070944" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-primary transition-colors">+1 (917) 807-0944</a>
                </div>
                <div className="flex items-center gap-3"><Phone className="h-5 w-5 text-primary" /><a href="tel:+18722970607" className="text-sm hover:text-primary transition-colors">+1 (872) 297-0607</a></div>
                <div className="flex items-center gap-3"><Mail className="h-5 w-5 text-primary" /><span className="text-sm">info@xenticsolutions.com</span></div>
              </div>

              <div className="mt-10 grid grid-cols-2 gap-4">
                {[
                  { icon: Clock, text: "Respond within 24hrs" },
                  { icon: Shield, text: "Zero obligation" },
                  { icon: Mail, text: "No spam, ever" },
                  { icon: Phone, text: "30-min call" },
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <t.icon className="h-4 w-4 text-primary" />{t.text}
                  </div>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              {submitted ? (
                <div className="glass-card rounded-2xl p-12 text-center">
                  <div className="text-4xl mb-4">🎉</div>
                  <h2 className="font-heading text-2xl font-bold mb-2">Thank You!</h2>
                  <p className="text-muted-foreground">We've received your information and will be in touch within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="glass-card rounded-2xl p-6 md:p-8 space-y-4 shadow-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label htmlFor="first_name">First Name *</Label><Input id="first_name" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required className="bg-secondary border-border" /></div>
                    <div><Label htmlFor="last_name">Last Name *</Label><Input id="last_name" value={form.last_name} onChange={(e) => update("last_name", e.target.value)} required className="bg-secondary border-border" /></div>
                  </div>
                  <div><Label htmlFor="email">Email *</Label><Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className="bg-secondary border-border" /></div>
                  <div><Label htmlFor="phone">Phone</Label><Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="bg-secondary border-border" /></div>
                  <div><Label htmlFor="business_name">Business Name</Label><Input id="business_name" value={form.business_name} onChange={(e) => update("business_name", e.target.value)} className="bg-secondary border-border" /></div>
                  <div>
                    <Label htmlFor="industry">Industry</Label>
                    <Select onValueChange={(v) => update("industry", v)}>
                      <SelectTrigger id="industry" aria-label="Industry" className="bg-secondary border-border"><SelectValue placeholder="Select your industry" /></SelectTrigger>
                      <SelectContent>
                        {["HVAC", "Healthcare", "Legal", "Real Estate", "E-Commerce", "Education", "Hospitality", "Accounting", "Other"].map((i) => (
                          <SelectItem key={i} value={i}>{i}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label htmlFor="biggest_challenge">Biggest Challenge</Label><Textarea id="biggest_challenge" value={form.biggest_challenge} onChange={(e) => update("biggest_challenge", e.target.value)} placeholder="What's your biggest challenge with leads or customer communication?" className="bg-secondary border-border" /></div>
                  <div>
                    <Label htmlFor="referral_source">How did you hear about us?</Label>
                    <Select onValueChange={(v) => update("referral_source", v)}>
                      <SelectTrigger id="referral_source" aria-label="How did you hear about us?" className="bg-secondary border-border"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>
                        {["Google Search", "Social Media", "Referral", "Blog", "Advertisement", "Other"].map((i) => (
                          <SelectItem key={i} value={i}>{i}</SelectItem>
                        ))}
                      </SelectContent>
                  </Select>
                  </div>

                  {/* Book a Call */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-primary/30 hover:border-primary/60 transition-colors h-10"
                    asChild
                  >
                    <a href="https://calendly.com/xenticsolutions-creativity/30min" target="_blank" rel="noopener noreferrer">
                      <CalendarDays className="mr-2 h-4 w-4 text-primary" />
                      <span>Book a Call</span>
                    </a>
                  </Button>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full border-0"
                    style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
                    disabled={loading}
                  >
                    <span className="text-white font-semibold">{loading ? "Submitting…" : "Book Free Consultation →"}</span>
                  </Button>
                </form>
              )}
            </ScrollReveal>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
